import React, { useState, useEffect, useMemo, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { bookingApi } from '../../../utils/bookingApi';
import { poundsToPence, formatPence } from '../../../utils/money';
import { Picker, useUnsavedChanges } from '../../../components/ui';
import { ContactSheet } from './AdminBusinessContacts';

const blankLine = (kind = 'CHARGE') => ({ kind, description: '', quantity: '1', unitPrice: '' });
const toRows = (lines) => (lines.length
  ? lines.map(l => ({
    kind: l.kind || 'CHARGE',
    ...(l.creditNoteId ? { creditNoteId: l.creditNoteId } : {}),
    description: l.description,
    quantity: String(l.quantity),
    unitPrice: (l.unitAmount / 100).toFixed(2),
  }))
  : [blankLine()]);

// Turns the editor rows into API lines, or returns { error }. Fully blank
// rows are ignored so an unused spare row doesn't block saving.
export function rowsToLines(rows) {
  const lines = [];
  for (const [i, r] of rows.entries()) {
    if (!r.description.trim() && !r.unitPrice.trim()) continue;
    const n = i + 1;
    if (!r.description.trim()) return { error: `Line ${n} needs a description.` };
    const quantity = Number(r.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return { error: `Line ${n} needs a whole-number quantity of 1 or more.` };
    const unitAmount = poundsToPence(r.unitPrice);
    if (!unitAmount) return { error: `Line ${n} needs a price above £0.` };
    lines.push({
      kind: r.kind === 'CREDIT' ? 'CREDIT' : 'CHARGE',
      ...(r.creditNoteId ? { creditNoteId: r.creditNoteId } : {}),
      description: r.description.trim(),
      quantity,
      unitAmount,
    });
  }
  return { lines };
}

// Editor for a new or draft invoice. Calls onSaved(invoice) after saving.
// renderActions({ save, saving, contact }) replaces the Save draft button.
export default function InvoiceDraftEditor({ invoice, contacts, defaultContactId, onSaved, onContactsChanged, renderActions, busy }) {
  const [contactId, setContactId] = useState(invoice?.businessContactId || defaultContactId || '');
  const [rows, setRows] = useState(toRows(invoice?.lines || []));
  const [dueDate, setDueDate] = useState(invoice?.dueDate ? invoice.dueDate.slice(0, 10) : '');
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [addingContact, setAddingContact] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availableNotes, setCreditNotes] = useState([]);
  const [notice, setNotice] = useState(null);

  // Unsaved changes: anything different from what was last loaded or saved
  const snapshot = () => JSON.stringify({ contactId, rows, dueDate, notes });
  const savedAs = useRef(null);
  if (savedAs.current === null) savedAs.current = snapshot();
  useUnsavedChanges(snapshot() !== savedAs.current);

  const contactItems = useMemo(() => contacts.map(c => ({
    id: c.id, label: c.name, detail: c.contactEmail || 'No email', search: c.contactEmail || '',
  })), [contacts]);

  const setRow = (i, key) => (e) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)));
  const rowTotal = (r) => (poundsToPence(r.unitPrice) || 0) * (Number.isInteger(Number(r.quantity)) ? Number(r.quantity) : 0);
  const sumOf = (kind) => rows.filter(r => r.kind === kind).reduce((sum, r) => sum + rowTotal(r), 0);
  const charges = sumOf('CHARGE');
  const credits = sumOf('CREDIT');
  const total = charges - credits;
  const hasCredit = rows.some(r => r.kind === 'CREDIT');

  const contact = contacts.find(c => c.id === contactId);
  const creditBalance = contact?.creditBalance || 0;

  // Standalone credit notes the chosen business can still spend
  useEffect(() => {
    let cancelled = false;
    setCreditNotes([]);
    if (!contactId || !(creditBalance > 0)) return undefined;
    bookingApi.getBusinessContact(contactId)
      .then(res => { if (!cancelled) setCreditNotes((res.data?.creditNotes || []).filter(n => n.available > 0)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contactId, creditBalance]);

  const noteById = (id) => availableNotes.find(n => n.id === id);

  const chooseContact = (id) => {
    if (id !== contactId && rows.some(r => r.creditNoteId)) {
      setRows(rs => {
        const kept = rs.filter(r => !r.creditNoteId);
        return kept.length ? kept : [blankLine()];
      });
      setNotice('Credit lines from the previous business were removed.');
    }
    setContactId(id);
  };

  const addNoteCredit = (note) => {
    setRows(rs => {
      const otherCredits = rs.filter(r => r.kind === 'CREDIT').reduce((sum, r) => sum + rowTotal(r), 0);
      const pence = Math.max(0, Math.min(note.available, charges - otherCredits));
      const row = {
        kind: 'CREDIT', creditNoteId: note.id, description: `Credit from ${note.creditNoteNumber}`,
        quantity: '1', unitPrice: pence > 0 ? (pence / 100).toFixed(2) : '',
      };
      // Replace a spare blank row rather than leave it beside the new one
      const spare = rs.length === 1 && !rs[0].description.trim() && !rs[0].unitPrice.trim();
      return spare ? [row] : [...rs, row];
    });
  };

  // Saves and returns the saved invoice, or null if invalid or failed.
  // forSend also refuses an invoice that doesn't come to more than £0.
  const save = async ({ forSend = false } = {}) => {
    setError(null);
    if (!contactId) { setError('Choose who the invoice is for.'); return null; }
    const { lines, error: lineError } = rowsToLines(rows);
    if (lineError) { setError(lineError); return null; }
    for (const r of rows) {
      const note = r.creditNoteId && noteById(r.creditNoteId);
      const l = r.creditNoteId && rowsToLines([r]).lines?.[0];
      if (note && l && l.quantity * l.unitAmount > note.available) {
        setError(`That is more than is left on ${note.creditNoteNumber}.`);
        return null;
      }
    }
    if (forSend) {
      const net = lines.reduce((sum, l) => sum + (l.kind === 'CREDIT' ? -1 : 1) * l.quantity * l.unitAmount, 0);
      if (net <= 0) { setError('An invoice must come to more than £0 to be sent.'); return null; }
    }
    const body = { businessContactId: contactId, dueDate: dueDate || null, notes, lines };
    setSaving(true);
    const sent = snapshot();
    try {
      const res = invoice ? await bookingApi.updateInvoice(invoice.id, body) : await bookingApi.createInvoice(body);
      savedAs.current = sent;
      onSaved(res.data);
      return res.data;
    } catch (err) {
      const status = err.response?.status;
      setError(!err.response || [502, 503, 504].includes(status)
        ? "The server didn't respond. Please try again in a moment."
        : (err.response.data?.error || "Couldn't save the invoice."));
      return null;
    } finally {
      setSaving(false);
    }
  };

  // A failure shows in the contact pop-up
  const addContact = async (data) => {
    const res = await bookingApi.createBusinessContact(data);
    await onContactsChanged();
    chooseContact(res.data.id);
    setAddingContact(false);
  };

  return (
    <div className="inv-form">
      <div className="inv-card">
        <div className="bk-label">
          <label htmlFor="invoice-bill-to">Bill to</label>
          <Picker id="invoice-bill-to" label="Bill to" items={contactItems} value={contactId} onChange={(id) => chooseContact(id || '')}
            placeholder="Search business contacts…" noneText="No contact matches. Add a new one below." />
        </div>
        {contact && !contact.contactEmail && <span className="inv-muted inv-small">This contact has no email, so you'll need to send the PDF yourself.</span>}
        {notice && <p className="inv-note" role="status">{notice}</p>}
        <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={() => setAddingContact(true)}>New contact</button>
        {addingContact && <ContactSheet title="New contact" onSave={addContact} onClose={() => setAddingContact(false)} />}
      </div>

      <div className="inv-card">
        <h2 className="bk-section-title">Lines</h2>
        {availableNotes.length > 0 && (
          <div className="inv-credit-box">
            <span>Credit on account: {formatPence(creditBalance)} available</span>
            {availableNotes.filter(n => !rows.some(r => r.creditNoteId === n.id)).map(n => (
              <span key={n.id} className="inv-actions">
                <span className="inv-small">{n.creditNoteNumber} · {formatPence(n.available)} left</span>
                <button type="button" className="bk-btn bk-btn--sm" aria-label={`Use ${n.creditNoteNumber}`} onClick={() => addNoteCredit(n)}>Use</button>
              </span>
            ))}
          </div>
        )}
        {rows.map((r, i) => {
          const linked = noteById(r.creditNoteId);
          const isCredit = r.kind === 'CREDIT';
          return (
            <div key={i} className="inv-line">
              <div className="inv-line__kind">
                <span className="inv-seg" role="group" aria-label={`Line ${i + 1} type`}>
                  {['CHARGE', 'CREDIT'].map(k => (
                    <button key={k} type="button" className="bk-btn bk-btn--sm inv-seg__btn" aria-pressed={r.kind === k}
                      disabled={!!r.creditNoteId && k === 'CHARGE'}
                      onClick={() => setRows(rs => rs.map((x, j) => (j === i ? { ...x, kind: k } : x)))}>
                      {k === 'CHARGE' ? 'Charge' : 'Credit'}
                    </button>
                  ))}
                </span>
                {r.creditNoteId && linked && (
                  <span className="inv-muted inv-small">from {linked.creditNoteNumber}, {formatPence(linked.available)} available</span>
                )}
              </div>
              <label className="inv-field inv-line__desc">
                <span className="inv-muted inv-small">Description</span>
                <input className="bk-input" aria-label={`Description ${i + 1}`} value={r.description} onChange={setRow(i, 'description')} />
              </label>
              <label className="inv-field">
                <span className="inv-muted inv-small">Qty</span>
                <input className="bk-input" type="number" min="1" step="1" inputMode="numeric" aria-label={`Quantity ${i + 1}`} value={r.quantity} readOnly={!!r.creditNoteId} onChange={setRow(i, 'quantity')} />
              </label>
              <label className="inv-field">
                <span className="inv-muted inv-small">Unit £</span>
                <input className="bk-input" type="number" min="0.01" step="0.01" inputMode="decimal" aria-label={`Unit price ${i + 1}`} value={r.unitPrice} onChange={setRow(i, 'unitPrice')} />
              </label>
              <span className="inv-actions">
                <span className={`inv-line__total${isCredit ? ' inv-line__total--credit' : ''}`}>{isCredit ? `-${formatPence(rowTotal(r))}` : formatPence(rowTotal(r))}</span>
                {rows.length > 1 && (
                  <button type="button" className="ui-icon-btn" aria-label={`Remove line ${i + 1}`} onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}>
                    <XMarkIcon aria-hidden="true" />
                  </button>
                )}
              </span>
            </div>
          );
        })}
        <div className="inv-actions">
          <button type="button" className="bk-btn bk-btn--sm" onClick={() => setRows(rs => [...rs, blankLine('CHARGE')])}>Add a charge</button>
          <button type="button" className="bk-btn bk-btn--sm" onClick={() => setRows(rs => [...rs, blankLine('CREDIT')])}>Add a credit</button>
        </div>
        <div className="inv-totals">
          <span className="inv-muted inv-small">{`Charges ${formatPence(charges)}`}</span>
          {hasCredit && <span className="inv-muted inv-small">{`Credits -${formatPence(credits)}`}</span>}
          <span className="inv-muted inv-small">Total</span><strong>{formatPence(total)}</strong>
        </div>
      </div>

      <div className="inv-card">
        <div className="inv-two">
          <label className="bk-label">
            <span>Due date <span className="bk-optional">(optional)</span></span>
            <input type="date" className="bk-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            <span className="inv-muted inv-small">Leave blank for 30 days after sending.</span>
          </label>
          <label className="bk-label">
            <span>Notes <span className="bk-optional">(optional)</span></span>
            <textarea className="bk-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Printed on the invoice" />
          </label>
        </div>
      </div>

      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {renderActions ? renderActions({ save, saving, contact }) : (
        <div className="bk-actions">
          <button type="button" className="bk-btn bk-btn--primary" disabled={saving || busy} onClick={() => save()}>Save draft</button>
        </div>
      )}
    </div>
  );
}
